import React, { useState, FC, useMemo, useCallback } from 'react';
import { Stack, Box, Typography, Tooltip, SelectChangeEvent } from '@mui/material';
import {
  Button,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  FlyoutContainer,
  LargeFormFieldPresentational,
  RadioButton,
  ComboboxOption,
  ComboBox
} from '@cmhc-schl/hearth-ui';
import { useTranslation } from 'react-i18next';
import DiscreteSlider from 'Feature/V2/Shared_Portfolio/components/DiscreteSlider';
import {
  PricingScenariosFlyoutProps,
  FormPricingScenarioErrors,
  Remark,
  PricingScenarioPaylod
} from './types';
import styles from './styles.module.scss';
import { coverageTypeMapping } from './utils/coverageTypeMapping';
import { replaceAllPercentageSymbols } from './utils/replaceAllPercentageSymbols';
import { replaceCommasWithDot } from './utils/replaceCommasWithDot';
import { coverageTypeDefault, marks, largeFormFieldMaxChar } from './config';
import { getFilterDropdownValues } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolTable/utils/GetFilterDropdownValues';
import InputField from 'Feature/V2/Shared_Portfolio/components/InputField';
import { formatPercentageInput } from 'Feature/V2/Shared_Portfolio/utils/formatPercentageInput';
import { useDecisionOverrideRationales } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/hooks/useDecisionOverrideRationales';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { getMasterPoolCmhcAccountNumber } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolDetails/utils/masterPoolDetailsUtils';
import {
  useAddPricingScenarioMutation,
  useModifyPricingScenarioMutation
} from '../../api/hooks/useMutations';

const premiumRateRationaleCodes = [7000, 7001, 9999];
const premiumDiscountRationaleCodes = [7002, 7003, 9999];
const premiumRateAndDiscountRationaleCodes = [7004, 7005, 9999];

const HbtMasterPoolPricingScenariosFlyout: FC<PricingScenariosFlyoutProps> = ({
  openPricingScenarioFlyout,
  togglePricingScenarioDrawer,
  isNewScenario,
  scenarioNumber,
  toggleToastNotification,
  setErrorNotification,
  setNotificationBannerConfig
}) => {
  const { t } = useTranslation('HbtMasterPoolPricingScenario');

  const [coverageType, setCoverageType] = useState<string | undefined>('');
  const [sliderValue, setSliderValue] = useState<number>(1);
  const [premiumRate, setPremiumRate] = useState<string>('');
  const [rationaleInput, setRationaleInput] = useState<string>('');
  const [premiumDiscount, setPremiumDiscount] = useState<string>('');
  const [formErrors, setFormErrors] = useState<FormPricingScenarioErrors>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [remark, setRemark] = useState<Remark>({ remarkID: undefined, remarkText: undefined });
  const [rationaleRequired, setRationaleRequired] = useState(false);
  const authenticationContext = useAuthenticationContext();
  const masterPoolCmhcAccountNumber = getMasterPoolCmhcAccountNumber();
  const [oneFieldRequired, setOneFieldRequired] = useState(false);

  const { data: decisionOverrideData } = useDecisionOverrideRationales(authenticationContext);

  const { mutateAsync: addPricingScenario } = useAddPricingScenarioMutation();
  const { mutateAsync: modifyPricingScenario } = useModifyPricingScenarioMutation();

  const rationaleOptions: ComboboxOption[] = useMemo(() => {
    if (!decisionOverrideData) return [];
    const options: ComboboxOption[] = [];

    const premiumRateOnly = premiumRate && !premiumDiscount;
    const premiumDiscountOnly = !premiumRate && premiumDiscount;
    const premiumRateAndDiscount = premiumRate && premiumDiscount;

    let dropdownOptions;

    if (premiumRateOnly) {
      dropdownOptions = decisionOverrideData.filter((item) =>
        premiumRateRationaleCodes.includes(item.id)
      );
    } else if (premiumDiscountOnly) {
      dropdownOptions = decisionOverrideData.filter((item) =>
        premiumDiscountRationaleCodes.includes(item.id)
      );
    } else if (premiumRateAndDiscount) {
      dropdownOptions = decisionOverrideData.filter((item) =>
        premiumRateAndDiscountRationaleCodes.includes(item.id)
      );
    }

    dropdownOptions?.forEach((r) => {
      const label = i18n.language === 'en' ? r.englishRationale : r.frenchRationale;
      const option: ComboboxOption = {
        value: `${r.id}`,
        label,
        isGroup: true,
        groupName: ''
      };

      options.push(option);
    });
    return options;
  }, [decisionOverrideData, i18n.language, premiumRate, premiumDiscount]);

  const coverageTypeOptions = useMemo(
    () => getFilterDropdownValues(coverageTypeDefault, coverageTypeMapping(t)),
    []
  );

  const handleCoverageTypeChange = useCallback((value?: string) => {
    setCoverageType(value);
    if (value) {
      setFormErrors((prev) => ({ ...prev, coverageType: false }));
    }
  }, []);

  const handleSliderValueChange = (event: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number);
  };

  const handlePremiumRateChange = (value: string) => {
    const { value: formattedValue, error } = formatPercentageInput(value, 20);
    setPremiumRate(formattedValue);
    if (error) {
      setFormErrors((prev) => ({ ...prev, premiumRate: t('premium-rate-error') }));
    } else if (formErrors.premiumRate && !error) {
      setFormErrors((prev) => ({ ...prev, premiumRate: '' }));
    }
    if (formattedValue && oneFieldRequired) setOneFieldRequired(false);
  };

  const handlePremiumRateBlur = () => {
    const separator = premiumRate.includes(',') ? ',' : '.';
    const input = premiumRate.endsWith(separator) ? premiumRate.slice(0, -1) : premiumRate;
    setPremiumRate(input === '' ? '' : replaceAllPercentageSymbols(input));
    if (input === '') {
      setRationaleInput('');
      setFormErrors((prev) => ({ ...prev, rationale: false }));
    }
  };

  const handlePremiumDiscountChange = (value: string) => {
    const { value: formattedValue, error } = formatPercentageInput(value, 99);
    setPremiumDiscount(formattedValue);
    if (error && formattedValue.length > 0) {
      setFormErrors((prev) => ({ ...prev, premiumDiscount: t('premium-discount-error') }));
    } else if (formErrors.premiumDiscount && !error) {
      setFormErrors((prev) => ({ ...prev, premiumDiscount: undefined }));
    }
    if (formattedValue && oneFieldRequired) setOneFieldRequired(false);
  };

  const handlePremiumDiscountBlur = () => {
    const separator = premiumDiscount.includes(',') ? ',' : '.';
    const input = premiumDiscount.endsWith(separator)
      ? premiumDiscount.slice(0, -1)
      : premiumDiscount;
    setPremiumDiscount(input === '' ? '' : replaceAllPercentageSymbols(input));
  };

  const handleRationaleInputChange = (value: string) => {
    setRationaleInput(value);
    if (value.length > largeFormFieldMaxChar) {
      setFormErrors((prev) => ({ ...prev, rationale: true }));
    } else if (formErrors.rationale && value.length <= largeFormFieldMaxChar) {
      setFormErrors((prev) => ({ ...prev, rationale: false }));
    }
  };

  const handleCancelFlyout = () => {
    setFormErrors({});
    setCoverageType('');
    setSliderValue(1);
    setPremiumRate('');
    setRationaleInput('');
    setPremiumDiscount('');
    togglePricingScenarioDrawer(false);
    setRationaleRequired(false);
    setOneFieldRequired(false);
    setSubmitting(false);
  };

  const handleRemarkChange = (remarks: ComboboxOption | null) => {
    if (remarks) {
      setRemark({
        remarkText: remarks.label,
        remarkID: +remarks.value
      });

      if (rationaleRequired) {
        setRationaleRequired(false);
      }
      return;
    }

    setRemark({
      remarkText: undefined,
      remarkID: undefined
    });
  };

  const handleAddPricingScenario = async (payload: PricingScenarioPaylod) => {
    try {
      const response = await addPricingScenario({
        authenticationContext,
        payload
      });
      if (response) {
        toggleToastNotification(true);
        setTimeout(() => {
          toggleToastNotification(false);
        }, 4000);
        setSubmitting(false);
        handleCancelFlyout();
      }
    } catch (error) {
      setNotificationBannerConfig({
        header: t('add-pricing-scenario-failed'),
        subHeader: t('add-pricing-scenario-notification-error-body')
      });
      setErrorNotification(true);
      setSubmitting(false);
      handleCancelFlyout();
    }
  };

  const handleModifyPricingScenario = async (payload: PricingScenarioPaylod) => {
    try {
      const response = await modifyPricingScenario({
        authenticationContext,
        payload
      });
      if (response) {
        toggleToastNotification(true);
        setTimeout(() => {
          toggleToastNotification(false);
        }, 4000);
        setSubmitting(false);
        handleCancelFlyout();
      }
    } catch (error) {
      setNotificationBannerConfig({
        header: t('modify-pricing-scenario-failed'),
        subHeader: t('add-pricing-scenario-notification-error-body')
      });
      setErrorNotification(true);
      setSubmitting(false);
      handleCancelFlyout();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isNewScenario && !premiumDiscount && !premiumRate && !oneFieldRequired) {
      setOneFieldRequired(true);
      return;
    }

    if (
      ((premiumRate || premiumDiscount) && !remark.remarkID) ||
      (!coverageType && isNewScenario)
    ) {
      if ((premiumRate || premiumDiscount) && !remark.remarkID) {
        setRationaleRequired(true);
      }

      if (!coverageType && !formErrors.coverageType && isNewScenario) {
        setFormErrors((prev) => ({ ...prev, coverageType: true }));
      }
      return;
    }

    if (remark.remarkID === 9999 && rationaleInput.length < 1) {
      setFormErrors((prev) => ({ ...prev, rationale: true }));
      return;
    }

    if (
      (formErrors.premiumRate && formErrors.premiumRate.length > 0) ||
      (formErrors.premiumDiscount && formErrors.premiumDiscount.length > 0) ||
      !!formErrors.rationale ||
      rationaleRequired ||
      formErrors.coverageType
    ) {
      return;
    }

    setFormErrors({});
    setRationaleRequired(false);

    const payload: PricingScenarioPaylod = {
      masterPoolAccountNumber: +masterPoolCmhcAccountNumber,
      premiumRate: premiumRate ? replaceCommasWithDot(premiumRate) : null,
      remarkId: remark.remarkID,
      remarkText: remark.remarkID === 9999 ? rationaleInput : (remark.remarkText ?? ''),
      premiumDiscount: premiumDiscount ? replaceCommasWithDot(premiumDiscount) : null
    };

    if (isNewScenario) {
      payload.termInsuranceYearCount = coverageType === 'full-amortization' ? null : sliderValue;
      setSubmitting(true);
      handleAddPricingScenario(payload);
    } else {
      payload.scenarioNumber = scenarioNumber;
      setSubmitting(true);
      handleModifyPricingScenario(payload);
    }
  };

  return (
    <FlyoutContainer
      id="pricing-scenario-flyout"
      isHeaderRequired={true}
      headerText={isNewScenario ? t('add-scenario') : t('modify-scenario-header')}
      open={openPricingScenarioFlyout}
      onClose={handleCancelFlyout}
      content={
        <form onSubmit={handleSubmit} className={styles.pricingScenarioFlyoutForm}>
          <Box role="presentation" className={styles.pricingScenarioInputsWrapper}>
            {isNewScenario && (
              <Box>
                <Typography
                  className={styles.pricingScenarioHeaderLabel}
                  data-testid="coverage-type-label"
                >
                  {t('coverage-type')}
                </Typography>
                <RadioButton
                  defaultOption={coverageType}
                  options={coverageTypeOptions}
                  onChange={handleCoverageTypeChange}
                  noDisplayErrorMessage={!formErrors.coverageType}
                  required={formErrors.coverageType}
                  errorMessage={t('coverage-type-inline-error')}
                />
              </Box>
            )}
            {coverageType === 'term-insurance' && isNewScenario && (
              <Box>
                <Typography
                  className={styles.pricingScenarioHeaderLabel}
                  data-testid="select-term-insurance-label"
                >
                  {t('select-term-insurance')}
                  &nbsp;
                  <Tooltip
                    title={t('select-term-insurance-tooltip')}
                    placement="top-start"
                    arrow
                    slotProps={{
                      tooltip: {
                        className: styles.pricingScenarioTermInsuranceToolTip
                      },
                      arrow: {
                        className: styles.tooltipArrow
                      }
                    }}
                  >
                    <div>
                      <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                    </div>
                  </Tooltip>
                </Typography>
                <Box className={styles.discreteSliderWrapper}>
                  <DiscreteSlider
                    sliderValue={sliderValue}
                    onSliderChange={handleSliderValueChange}
                    min={1}
                    max={5}
                    marks={marks}
                    data-testid="term-insurance-slider"
                  />
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: 'start',
                    alignItems: 'start'
                  }}
                  className={styles.bannerContainer}
                >
                  <CmhcIcon
                    className={styles.bannerIcon}
                    size={CmhcIconSize.MEDIUM}
                    type={'icon_info_outlined'}
                  />
                  <Stack direction="row" spacing={1} className={styles.bannerText}>
                    <Typography>
                      <span className={styles.title}>{t('Term-Insurance-Flyout-Banner')}</span>
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            )}
            <Box className={styles.fieldWrapper}>
              <Box className={styles.premiumRateInput}>
                <InputField
                  id="premium-rate"
                  data-testid="premium-rate"
                  label={isNewScenario ? t('premium-rate-header') : t('modify-premium-rate-header')}
                  value={premiumRate}
                  onChange={(value) => handlePremiumRateChange(value)}
                  error={formErrors.premiumRate ?? ''}
                  placeholder={t('premium-rate-placeholder')}
                  errorStatus={!!formErrors.premiumRate || oneFieldRequired}
                  onBlur={handlePremiumRateBlur}
                />
              </Box>
              <Box className={styles.premiumDiscountInput}>
                <InputField
                  id={'premium-discount'}
                  label={
                    isNewScenario
                      ? t('premium-discount-header')
                      : t('modify-premium-discount-header')
                  }
                  value={premiumDiscount}
                  onChange={(value) => handlePremiumDiscountChange(value)}
                  onBlur={handlePremiumDiscountBlur}
                  error={formErrors.premiumDiscount ?? ''}
                  placeholder={t('premium-discount-placeholder')}
                  errorStatus={!!formErrors.premiumDiscount || oneFieldRequired}
                />
                {premiumDiscount && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'start',
                      alignItems: 'start'
                    }}
                    className={styles.bannerContainer}
                  >
                    <CmhcIcon
                      className={styles.bannerIcon}
                      size={CmhcIconSize.MEDIUM}
                      type={'icon_info_outlined'}
                    />
                    <Stack direction="row" spacing={1} className={styles.bannerText}>
                      <Typography>
                        <span className={styles.title}>{t('Premium-Discount-Flyout-Banner')}</span>
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Box>
              {oneFieldRequired && (
                <div className={styles.fieldErrorContainer}>
                  <CmhcIcon
                    size={CmhcIconSize.SMALL}
                    type="icon_info_outlined"
                    className={styles.errorInfoIcon}
                  />
                  <span role="alert" className={styles.errorMessageText}>
                    {t('one-field-mandatory-error')}
                  </span>
                </div>
              )}
              {(premiumRate || premiumDiscount) && (
                <Box>
                  <Typography className={styles.pricingScenarioHeaderLabel}>
                    {t('rationale-header')}
                  </Typography>
                  <Box className={styles.actionsDropdown}>
                    <ComboBox
                      ariaText={t('select-an-option')}
                      className={styles.modifyRemarksOptions}
                      placeholderText={t('select-an-option')}
                      options={rationaleOptions}
                      onChange={(e, v) => handleRemarkChange(v)}
                      error={rationaleRequired}
                      errorMessage={t('required-field')}
                    />
                  </Box>
                  {remark.remarkID === 9999 && (
                    <Box className={styles.rationaleInput}>
                      <LargeFormFieldPresentational
                        data-testid="large-form-field-pricing-scenario"
                        id={'large-form-field-pricing-scenario'}
                        ariaText="form"
                        value={rationaleInput}
                        invalid={!!formErrors.rationale}
                        error={
                          formErrors.rationale
                            ? rationaleInput.length > largeFormFieldMaxChar
                              ? t('input-maxlength-error')
                              : t('rationale-no-text-error')
                            : ''
                        }
                        maxLength={largeFormFieldMaxChar}
                        onChange={(e) => handleRationaleInputChange(e.target.value)}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box className={styles.flyoutFooter}>
            <Stack direction="row" spacing={3}>
              <Button
                data-test-id="cancel"
                type="button"
                name={'cancel'}
                onClick={handleCancelFlyout}
                className={styles.cancelButton}
                variant={ButtonVariant.SECONDARY}
                text={{
                  value: t('cancel-button')
                }}
                ariaText={{
                  value: t('cancel-button')
                }}
                role="button"
              />
              <Button
                data-test-id="show-result"
                type="submit"
                name={'add-scenario-button'}
                className={styles.submitButton}
                variant={ButtonVariant.PRIMARY}
                loading={submitting}
                text={{
                  value: isNewScenario ? t('add-scenario-button') : t('modify-scenario-button')
                }}
                ariaText={{
                  value: isNewScenario ? t('add-scenario-button') : t('modify-scenario-button')
                }}
                role="button"
              />
            </Stack>
          </Box>
        </form>
      }
    />
  );
};

export default HbtMasterPoolPricingScenariosFlyout;
